import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, KeyRound, Mail } from "lucide-react";
import { api, errMsg, RUOLI } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Field } from "@/components/Common";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const AREA = { superadmin: "Studio Principale", studio: "Studio", company: "Azienda" };

const ScopeFields = ({ f, setF, studios, companies }) => (
  <>
    <Field label="Area">
      <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
        <SelectTrigger data-testid="user-role-area-select"><SelectValue /></SelectTrigger>
        <SelectContent>{Object.entries(AREA).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
      </Select>
    </Field>
    {f.role === "studio" && (
      <Field label="Studio">
        <Select value={f.studio_id || undefined} onValueChange={(v) => setF({ ...f, studio_id: v })}>
          <SelectTrigger data-testid="user-studio-select"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
          <SelectContent>{studios.map((s) => <SelectItem key={s.id} value={s.id}>{s.denominazione}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
    )}
    {f.role === "company" && (
      <Field label="Azienda">
        <Select value={f.company_id || undefined} onValueChange={(v) => setF({ ...f, company_id: v })}>
          <SelectTrigger data-testid="user-company-select"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
          <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.denominazione}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
    )}
  </>
);

export default function UsersPage() {
  const { user } = useAuth();
  const sa = user.role === "superadmin";
  const [items, setItems] = useState([]);
  const [studios, setStudios] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [f, setF] = useState(null);
  const [pwd, setPwd] = useState(null);

  const load = useCallback(() => api.get("/users").then((r) => setItems(r.data)), []);
  useEffect(() => {
    load();
    if (sa) {
      api.get("/admin/studios").then((r) => setStudios(r.data));
      api.get("/admin/companies").then((r) => setCompanies(r.data));
    }
  }, [load, sa]);

  const run = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const create = () => run(async () => { await api.post("/users", f); setF(null); }, "Utente creato");

  return (
    <div>
      <PageHeader title="Utenti" subtitle={sa ? "Tutti gli accessi della piattaforma" : `Accessi di ${user.scope_name}`}>
        <Button data-testid="add-user-button" className="bg-sky-600 hover:bg-sky-700" onClick={() => setF({ email: "", name: "", password: "", tenant_role: "operatore", role: sa ? "company" : undefined })}>
          <Plus className="mr-2 h-4 w-4" />Nuovo utente
        </Button>
      </PageHeader>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <Table data-testid="users-table">
          <TableHeader><TableRow className="bg-slate-50">
            <TableHead>Nome</TableHead><TableHead>Email</TableHead>{sa && <TableHead>Area</TableHead>}<TableHead>Ruolo</TableHead><TableHead>Attivo</TableHead><TableHead />
          </TableRow></TableHeader>
          <TableBody>
            {items.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell className="text-sm">{u.email}</TableCell>
                {sa && <TableCell className="text-sm"><span className="text-xs text-slate-500">{AREA[u.role]}</span><br />{u.scope_name}</TableCell>}
                <TableCell>
                  {u.role === "superadmin" ? <span className="text-sm">Super amministratore</span> : (
                    <Select value={u.tenant_role} disabled={u.id === user.id} onValueChange={(v) => run(() => api.put(`/users/${u.id}`, { tenant_role: v }), "Ruolo aggiornato")}>
                      <SelectTrigger data-testid={`user-tenant-role-${u.id}`} className="h-8 w-40"><SelectValue /></SelectTrigger>
                      <SelectContent>{Object.entries(RUOLI).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                  {u.tenant_role === "agente" && (
                    <div className="mt-1 flex items-center gap-1 text-xs text-slate-600">Provvigione
                      <Input type="number" min="0" max="100" step="0.5" data-testid={`user-commission-${u.id}`} defaultValue={u.provvigione ?? 0} className="h-7 w-20"
                        onBlur={(e) => Number(e.target.value) !== (u.provvigione ?? 0) && run(() => api.put(`/users/${u.id}`, { provvigione: Number(e.target.value) }), "Provvigione aggiornata")} />%
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <Switch data-testid={`user-active-${u.id}`} disabled={u.id === user.id} checked={u.active !== false} onCheckedChange={(v) => run(() => api.put(`/users/${u.id}`, { active: v }), v ? "Utente attivato" : "Utente disattivato")} />
                </TableCell>
                <TableCell className="whitespace-nowrap text-right">
                  <Button variant="ghost" size="icon" data-testid={`user-reset-pwd-${u.id}`} onClick={() => setPwd({ id: u.id, password: "" })}><KeyRound className="h-4 w-4" /></Button>
                  {u.id !== user.id && !u.demo && (
                    <Button variant="ghost" size="icon" title="Invia email di benvenuto" data-testid={`user-invite-${u.id}`} onClick={() => run(() => api.post(`/users/${u.id}/invite`), `Email di benvenuto inviata a ${u.email}`)}><Mail className="h-4 w-4" /></Button>
                  )}
                  {u.id !== user.id && <ConfirmDelete testid={`delete-user-${u.id}`} onConfirm={() => run(() => api.delete(`/users/${u.id}`), "Utente eliminato")} />}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!f} onOpenChange={(o) => !o && setF(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nuovo utente</DialogTitle></DialogHeader>
          {f && (
            <div className="space-y-4">
              {sa && <ScopeFields f={f} setF={setF} studios={studios} companies={companies} />}
              <Field label="Nome"><Input data-testid="new-user-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
              <Field label="Email"><Input data-testid="new-user-email-input" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
              <Field label="Password iniziale (min. 8)"><Input data-testid="new-user-password-input" type="password" autoComplete="new-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} /></Field>
              {f.role !== "superadmin" && (
                <Field label="Ruolo">
                  <Select value={f.tenant_role} onValueChange={(v) => setF({ ...f, tenant_role: v })}>
                    <SelectTrigger data-testid="new-user-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(RUOLI).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
              )}
            </div>
          )}
          <DialogFooter><Button data-testid="create-user-submit" onClick={create}>Crea utente</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pwd} onOpenChange={(o) => !o && setPwd(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reimposta password</DialogTitle></DialogHeader>
          <Field label="Nuova password (min. 8)"><Input data-testid="reset-password-input" type="password" autoComplete="new-password" value={pwd?.password || ""} onChange={(e) => setPwd({ ...pwd, password: e.target.value })} /></Field>
          <DialogFooter>
            <Button data-testid="reset-password-submit" onClick={() => run(async () => { await api.put(`/users/${pwd.id}`, { password: pwd.password }); setPwd(null); }, "Password reimpostata")}>Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
